import type { AppStore } from '../core/store';
import type { AppState } from '../core/types';
import type { WorkspaceModule } from '../presentation/workspace';
import { DOCK_PANE_TITLES, type DockPaneId } from './shellPanes';

export type ShellCommand = 'import-master' | 'import-hydric' | 'edit-geometry' | 'filter-open' | 'focus-search' | 'export-geojson' | 'export-csv' | 'practical-cases' | 'topography-reference' | 'insert-text';
export interface ShellActions {
  readonly command: (command: ShellCommand, source: HTMLElement) => void;
  readonly viewLevel: (level: AppState['entityViewLevel']) => void;
}

/** Persisted compatibility mapping: the active right-dock pane is stored as the legacy workspace module. */
const MODULE_FOR_PANE: Readonly<Record<DockPaneId, WorkspaceModule>> = {
  properties: 'selection', classify: 'symbology', labels: 'labels', style: 'symbology', 'layer-style': 'symbology', data: 'data',
  presentation: 'presentation', legend: 'presentation', scenarios: 'symbology', export: 'export', project: 'project', draw: 'project', planning: 'project', subareas: 'project', saved: 'selection', scope: 'selection',
};
const PANE_FOR_MODULE: Readonly<Record<WorkspaceModule, DockPaneId>> = {
  project: 'project', selection: 'properties', data: 'data', symbology: 'classify', labels: 'labels', presentation: 'presentation', export: 'export',
};

/**
 * Owns shell chrome behaviour only: menus, docks, panes, proxy clicks and mobile drawers.
 * It never touches GIS engines; commands are delegated to App.
 */
export class ShellController {
  private pane: DockPaneId = 'properties';
  private readonly shell: HTMLElement;

  constructor(private readonly root: ParentNode, private readonly store: AppStore, private readonly actions: ShellActions) {
    this.shell = root.querySelector<HTMLElement>('.app-shell')!;
    this.shell.addEventListener('click', (event) => this.onClick(event));
    document.addEventListener('pointerdown', (event) => { if (!(event.target as Element).closest?.('.menubar-entry')) this.closeMenus(); });
    document.addEventListener('keydown', (event) => this.onKey(event));
    this.root.querySelectorAll<HTMLElement>('.menubar-entry').forEach((entry) => entry.addEventListener('pointerenter', () => {
      if (this.root.querySelector('.menubar-entry.open') && !entry.classList.contains('open')) this.openMenu(entry.dataset.menu!);
    }));
    store.subscribe((state, previous) => {
      if (state.activeWorkspaceModule !== previous.activeWorkspaceModule && MODULE_FOR_PANE[this.pane] !== state.activeWorkspaceModule) this.openPane(PANE_FOR_MODULE[state.activeWorkspaceModule] ?? 'properties', false);
    });
    const initial = store.getState().activeWorkspaceModule;
    this.openPane(initial === 'presentation' ? 'presentation' : 'properties', false);
  }

  get activePane(): DockPaneId { return this.pane; }

  openPane(id: DockPaneId, reveal = true): void {
    this.pane = id;
    this.root.querySelectorAll<HTMLElement>('.dock-pane[data-pane]').forEach((element) => { element.hidden = element.dataset.pane !== id; });
    const title = this.root.querySelector<HTMLElement>('#dock-title'); if (title) title.textContent = DOCK_PANE_TITLES[id];
    const back = this.root.querySelector<HTMLElement>('#dock-back'); if (back) back.hidden = id === 'properties';
    this.shell.dataset.activePane = id;
    const module = MODULE_FOR_PANE[id];
    if (this.store.getState().activeWorkspaceModule !== module) this.store.update({ activeWorkspaceModule: module }, { markDirty: false });
    if (reveal) this.revealRightDock();
  }

  setLeftTab(tab: 'layers' | 'explorer'): void {
    this.shell.dataset.leftTab = tab;
    this.root.querySelectorAll<HTMLElement>('[data-left-pane]').forEach((pane) => { pane.hidden = pane.dataset.leftPane !== tab; });
    this.root.querySelectorAll<HTMLElement>('[role="tab"][data-left-tab]').forEach((button) => button.setAttribute('aria-selected', String(button.dataset.leftTab === tab)));
  }

  revealRightDock(): void {
    const layout = this.store.getState().workspaceLayout;
    if (layout.rightPanelCollapsed) this.store.update({ workspaceLayout: { ...layout, rightPanelCollapsed: false } }, { markDirty: false });
    if (this.isCompact()) this.shell.dataset.mobileDock = 'right';
  }

  revealLeftDock(tab?: 'layers' | 'explorer'): void {
    if (tab) this.setLeftTab(tab);
    const layout = this.store.getState().workspaceLayout;
    if (layout.leftPanelCollapsed) this.store.update({ workspaceLayout: { ...layout, leftPanelCollapsed: false } }, { markDirty: false });
    if (this.isCompact()) this.shell.dataset.mobileDock = 'left';
  }

  closeMenus(): void {
    this.root.querySelectorAll<HTMLElement>('.menubar-entry.open').forEach((entry) => {
      entry.classList.remove('open');
      entry.querySelector('[data-menu-trigger]')?.setAttribute('aria-expanded', 'false');
      const popover = entry.querySelector<HTMLElement>('.menu-popover'); if (popover) popover.hidden = true;
    });
  }

  private openMenu(id: string): void {
    this.closeMenus();
    const entry = this.root.querySelector<HTMLElement>(`.menubar-entry[data-menu="${id}"]`); if (!entry) return;
    entry.classList.add('open');
    entry.querySelector('[data-menu-trigger]')?.setAttribute('aria-expanded', 'true');
    const popover = entry.querySelector<HTMLElement>('.menu-popover'); if (popover) popover.hidden = false;
    this.syncMenuState(entry);
  }

  /** Reflects the current view level in the Ver menu. */
  private syncMenuState(entry: HTMLElement): void {
    const level = this.store.getState().entityViewLevel;
    entry.querySelectorAll<HTMLElement>('[data-view-level-command]').forEach((item) => item.classList.toggle('checked', item.dataset.viewLevelCommand === level));
  }

  private isCompact(): boolean { return typeof window !== 'undefined' && window.matchMedia?.('(max-width: 760px)').matches === true; }

  private onClick(event: Event): void {
    const target = event.target as Element;
    const trigger = target.closest<HTMLElement>('[data-menu-trigger]');
    if (trigger) {
      const open = trigger.closest('.menubar-entry')?.classList.contains('open');
      if (open) this.closeMenus(); else this.openMenu(trigger.dataset.menuTrigger!);
      return;
    }
    const inMenu = Boolean(target.closest('.menu-popover'));
    const tab = target.closest<HTMLElement>('[role="tab"][data-left-tab]');
    if (tab) { this.setLeftTab(tab.dataset.leftTab as 'layers' | 'explorer'); return; }
    const proxy = target.closest<HTMLElement>('[data-proxy-click]');
    if (proxy) { this.closeMenus(); this.root.querySelector<HTMLElement>(proxy.dataset.proxyClick!)?.click(); return; }
    const paneButton = target.closest<HTMLElement>('[data-open-pane]');
    if (paneButton) { this.closeMenus(); this.openPane(paneButton.dataset.openPane as DockPaneId); return; }
    const level = target.closest<HTMLElement>('[data-view-level-command]');
    if (level) { this.closeMenus(); this.actions.viewLevel(level.dataset.viewLevelCommand as AppState['entityViewLevel']); return; }
    const command = target.closest<HTMLElement>('[data-command]');
    if (command) { this.closeMenus(); this.actions.command(command.dataset.command as ShellCommand, command); return; }
    if (target.closest('#dock-back')) { this.openPane('properties', false); return; }
    const mobileOpen = target.closest<HTMLElement>('[data-mobile-open]');
    if (mobileOpen) { this.shell.dataset.mobileDock = mobileOpen.dataset.mobileOpen; return; }
    if (target.closest('[data-mobile-close]')) { delete this.shell.dataset.mobileDock; return; }
    if (inMenu && target.closest('.menu-item')) this.closeMenus();
  }

  private onKey(event: KeyboardEvent): void {
    if (event.key === 'Escape') { if (this.root.querySelector('.menubar-entry.open')) { this.closeMenus(); event.stopPropagation(); } return; }
    const meta = event.ctrlKey || event.metaKey;
    if (!meta) return;
    const key = event.key.toLowerCase();
    if (key === 'k') { event.preventDefault(); this.actions.command('focus-search', this.shell); }
    else if (key === 's') { event.preventDefault(); this.root.querySelector<HTMLElement>(event.shiftKey ? '#save-project-as' : '#save-project')?.click(); }
    else if (key === 'o') { event.preventDefault(); this.root.querySelector<HTMLElement>('#open-project')?.click(); }
  }
}
